/**
 * CrmEventTriggerPreview - Preview card for CRM event triggers
 *
 * Displays CRM event information that triggered a proposal, such as
 * meeting notes with summary, action items, and key topics.
 */

import { Calendar, LayoutRight } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { timeParts, useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { ActionEntityPreview } from './ActionEntityPreview';
import { isTriggerButtonRecordOpen } from './ActionOverview.helpers';
import { ENTITY_CONTENT_REGISTRY } from './CreateActionCard.helpers';
import {
  EmailProviderButton,
  EmailThreadBody,
  EmailThreadHeader,
  EmailThreadPreviewBody,
} from './CrmEventTriggerPreview/EmailThreadCard';
import {
  GenericCrmHeader,
  GenericCrmPreviewBody,
} from './CrmEventTriggerPreview/GenericEntityCard';
import {
  getGenericPreviewFields,
  getStringValue,
} from './CrmEventTriggerPreview/helpers';
import {
  MeetingBody,
  MeetingHeader,
  MeetingPreviewBody,
} from './CrmEventTriggerPreview/MeetingCard';
import {
  MeetingNotesBody,
  MeetingNotesHeader,
  MeetingNotesPreviewBody,
} from './CrmEventTriggerPreview/MeetingNotesCard';
import {
  ResearchBody,
  ResearchHeader,
  ResearchPreviewBody,
} from './CrmEventTriggerPreview/ResearchCard';
import {
  extractSenderEmail,
  extractSenderName,
  getEntityTypeIcon,
  SenderAvatar,
  TimelineConnector,
  TriggerCardIconButton,
} from './shared';
import { DetailPanelCard } from './shared/DetailPanelCard';
import { resolveTriggerFromStore } from './shared/resolveTriggerEntity';

interface CrmEventTriggerPreviewProps {
  /** CRM trigger data from API */
  trigger: CrmEventTriggerData;
  /** Organization ID for entity navigation */
  orgId?: number;
  /** Whether to show the connecting line below the card (default: true) */
  showConnector?: boolean;
  /** Whether the card starts expanded (default: false) */
  defaultExpanded?: boolean;
  /**
   * Callback to open the same `kind:'email_thread'` slideout the draft's
   * "View thread" button opens. When provided (and this trigger is an email
   * thread), takes over the side affordance button in place of
   * `openSlideout`, so the card opens the thread slideout instead of the
   * generic entity record.
   */
  onOpenThread?: () => void;
}

/**
 * Loading skeleton matching the CRM preview layout
 */
export function CrmEventTriggerPreviewSkeleton() {
  return (
    <div className='mb-0'>
      <DetailPanelCard className='p-4 animate-pulse'>
        <div className='flex items-start gap-3'>
          {/* Icon skeleton */}
          <div className='w-8 h-8 bg-secondary rounded-lg flex-shrink-0' />
          <div className='flex-1 min-w-0'>
            {/* Title line */}
            <div className='flex items-center gap-2 mb-1'>
              <div className='h-4 bg-secondary rounded w-40' />
              <div className='h-3 bg-secondary rounded w-16' />
            </div>
            {/* Content lines */}
            <div className='space-y-1'>
              <div className='h-3 bg-secondary rounded w-full' />
              <div className='h-3 bg-secondary rounded w-3/4' />
            </div>
          </div>
        </div>
      </DetailPanelCard>
      {/* Connecting line */}
      <TimelineConnector />
    </div>
  );
}

function crmEventTriggerLabel({ isExpanded }: { isExpanded: boolean }) {
  return isExpanded ? 'Collapse CRM event preview' : 'Expand CRM event preview';
}

/**
 * Main CRM event trigger preview component.
 *
 * Composes the shared `<Accordion>` / `<AccordionItem>` so this card
 * inherits the disclosure mechanics (aria-expanded, keyboard handling,
 * controlled `isExpanded` / `defaultExpanded` API), design-system focus
 * styling, and — crucially — smooth grid-rows expand/collapse animation.
 *
 * Architecture: each per-entity-type preview is split into two
 * components — a Header (always visible in the trigger row) and a
 * Body (mounted lazily inside the AccordionItem panel when expanded).
 * That split is what lets the panel actually animate: the header gives
 * the trigger row its content; the body gives the panel a real height
 * to grow into when the user clicks expand. The `lazy` prop on
 * AccordionItem keeps body components dormant until first expand so
 * cards on a long list don't pre-render their iframes / heavy subtrees.
 * Each variant's Header/PreviewBody/Body trio lives in its own file
 * under `CrmEventTriggerPreview/` (meeting notes, meeting, research,
 * email thread, generic) — this file only dispatches to them.
 *
 * The `endContent` slot holds the side-action buttons (Open in Gmail,
 * Open in CRM) as siblings of the trigger button so click events on
 * those buttons don't toggle the disclosure.
 */
export default function CrmEventTriggerPreview({
  trigger,
  orgId,
  showConnector = true,
  defaultExpanded = false,
  onOpenThread,
}: CrmEventTriggerPreviewProps) {
  const { timeFormat } = useOrgTimeFormat();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Lazy-mount the body: render it the first time the disclosure
  // expands and keep it mounted thereafter (so collapse still animates
  // from real content). Heavier preview bodies (email iframes, full
  // markdown) shouldn't run for every collapsed card on a long list.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    if (isExpanded && !hasMounted) {
      setHasMounted(true);
    }
  }, [isExpanded, hasMounted]);
  const slideoutContext = useSlideoutStackContextOptional();
  // Origin-aware open (see useOpenSlideout): this card renders inside the
  // proposal panel, so opening the record REPLACES anything layered above
  // the proposal instead of stacking.
  const openSlideout = useOpenSlideoutOptional();

  const entityType = trigger.crm_event.entity_type_name;
  const isMeetingNotes = entityType === 'ahoy_meeting_notes';
  const isMeeting = entityType === 'ahoy_meeting';
  const isEmailThread = entityType === 'ahoy_email_thread';
  const isResearch = entityType === 'ahoy_research';

  // Highlight the side affordance while this trigger's target is open.
  // Email threads (with onOpenThread wired) are opened as a `email_thread`
  // slideout entry — mirror EmailTriggerCard's kind-presence check. Every
  // other trigger uses the standard entity-target open-state check (same
  // affordance as the header's record buttons).
  const isRecordOpen =
    isEmailThread && onOpenThread
      ? !!slideoutContext?.stack.some(entry => entry.kind === 'email_thread')
      : isTriggerButtonRecordOpen(
          { kind: 'entity', entityId: trigger.crm_event.entity_id },
          slideoutContext?.stack
        );

  const handleOpenInCrm = useCallback(() => {
    if (isEmailThread && onOpenThread) {
      // ProposalDetailPanel's handleOpenThread already toggles closed when
      // an email_thread entry is on the stack — no need to duplicate that
      // here.
      onOpenThread();
      return;
    }
    // Toggle: this button already highlights via `isRecordOpen` above — a
    // second click on a highlighted button closes the record (and anything
    // layered above it) rather than re-opening it.
    if (isRecordOpen) {
      slideoutContext?.closeFromEntry(trigger.crm_event.entity_id);
      return;
    }
    if (openSlideout) {
      openSlideout(trigger.crm_event.entity_id);
    }
  }, [
    isEmailThread,
    onOpenThread,
    isRecordOpen,
    slideoutContext,
    openSlideout,
    trigger.crm_event.entity_id,
  ]);
  const isRegistryCovered = entityType in ENTITY_CONTENT_REGISTRY;
  const emailThreadId = isEmailThread
    ? getStringValue(trigger.crm_event.event, 'thread_id')
    : null;
  const emailProvider = isEmailThread
    ? getStringValue(trigger.crm_event.event, 'provider')
    : null;

  // Get dynamic entity icon
  const EntityIcon = getEntityTypeIcon(entityType);

  // Prefer live CRM-store data over the endpoint's point-in-time snapshot for
  // NON-email triggers (their fields are entity properties). Email threads are
  // skipped — their body/participants live in timeline events, not the store —
  // so we don't fetch their entity and keep rendering from the snapshot.
  const storeObject = useCrmObjectSnapshot(
    isEmailThread ? undefined : trigger.crm_event.entity_id,
    orgId
  );
  const effectiveTrigger = useMemo(
    () =>
      isEmailThread ? trigger : resolveTriggerFromStore(trigger, storeObject),
    [trigger, storeObject, isEmailThread]
  );

  // Header component — always visible in the trigger row.
  const renderHeader = () => {
    if (isMeetingNotes) {
      return <MeetingNotesHeader data={effectiveTrigger} />;
    }
    if (isMeeting) {
      return <MeetingHeader data={effectiveTrigger} />;
    }
    if (isEmailThread) {
      return <EmailThreadHeader data={effectiveTrigger} />;
    }
    if (isResearch) {
      return <ResearchHeader data={effectiveTrigger} />;
    }
    if (isRegistryCovered) {
      return (
        <ActionEntityPreview
          entityId={effectiveTrigger.crm_event.entity_id}
          entityType={entityType}
          label={effectiveTrigger.crm_event.display_name}
          enrichedData={undefined}
          orgId={orgId}
        />
      );
    }
    return (
      <GenericCrmHeader data={effectiveTrigger} entityTypeName={entityType} />
    );
  };

  // Body component — only mounts when the disclosure is expanded
  // (`lazy` prop on the AccordionItem). Returns null when there is no
  // expandable body (generic entity types are header-only).
  const renderBody = (): ReactNode => {
    if (isMeetingNotes) {
      return <MeetingNotesBody data={effectiveTrigger} orgId={orgId} />;
    }
    if (isMeeting) {
      return <MeetingBody data={effectiveTrigger} />;
    }
    if (isEmailThread) {
      return <EmailThreadBody data={effectiveTrigger} orgId={orgId} />;
    }
    if (isResearch) {
      return <ResearchBody data={effectiveTrigger} orgId={orgId} />;
    }
    return null;
  };

  // Compact preview rendered into AccordionItem.previewContent so the
  // collapsed card matches the original layout (snippet / compact meta
  // visible by default). Animates closed when the user expands the
  // disclosure while the main panel animates open over the same window.
  // Compute generic preview fields up-front so the parent's
  // "is the card expandable?" decision can use the actual presence of
  // content (not just the truthiness of a JSX element, which is always
  // truthy even when the component returns null at render time).
  const isFirstClassType =
    isMeetingNotes || isMeeting || isEmailThread || isResearch;
  const genericPreviewFields = useMemo(
    () =>
      isFirstClassType || isRegistryCovered
        ? []
        : getGenericPreviewFields(effectiveTrigger),
    [isFirstClassType, isRegistryCovered, effectiveTrigger]
  );

  const renderPreview = (): ReactNode => {
    if (isMeetingNotes) {
      return <MeetingNotesPreviewBody data={effectiveTrigger} orgId={orgId} />;
    }
    if (isMeeting) {
      return <MeetingPreviewBody data={effectiveTrigger} />;
    }
    if (isEmailThread) {
      return <EmailThreadPreviewBody data={effectiveTrigger} />;
    }
    if (isResearch) {
      return <ResearchPreviewBody data={effectiveTrigger} orgId={orgId} />;
    }
    return genericPreviewFields.length > 0 ? (
      <GenericCrmPreviewBody fields={genericPreviewFields} />
    ) : null;
  };
  const previewNode = renderPreview();
  // First-class types always have an expandable body (their dedicated
  // Body component renders meaningful content). For generic types, the
  // card is only expandable when there are preview fields to show — the
  // panel is empty otherwise, so the chevron is suppressed.
  const isExpandable = isFirstClassType || genericPreviewFields.length > 0;

  const cardIcon = isEmailThread ? (
    <div className='flex-shrink-0'>
      <SenderAvatar
        email={extractSenderEmail(
          getStringValue(trigger.crm_event.event, 'from') ||
            trigger.crm_event.display_name
        )}
        name={extractSenderName(
          getStringValue(trigger.crm_event.event, 'from') ||
            trigger.crm_event.display_name
        )}
      />
    </div>
  ) : (
    // Icon chips inside content are neutral — brand is reserved for the five
    // slots in VISUAL_STYLE_GUIDE.md § Brand. Tokens auto-flip, so no `dark:`.
    <div className='w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0'>
      {isMeeting ? (
        <Calendar className='w-4 h-4 text-fg-quaternary' />
      ) : (
        <EntityIcon className='w-4 h-4 text-fg-quaternary' aria-hidden='true' />
      )}
    </div>
  );

  // For email-thread triggers, the time appears in the same flex group as
  // the side buttons (matches the original layout — time, Gmail, Open in
  // CRM, chevron all visually adjacent on the right edge of the row).
  // Other entity types render the time inside their per-type Header
  // alongside meta like attendees / source.
  const formattedEmailDateTime = isEmailThread
    ? (() => {
        const dateStr = getStringValue(trigger.crm_event.event, 'date');
        if (!dateStr) {
          return null;
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
          return null;
        }
        return d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          // Shared time options — midnight-safe hour cycle, org-aware format.
          ...timeParts(timeFormat),
        });
      })()
    : null;

  return (
    <div className='mb-0'>
      <DetailPanelCard>
        {/* Standalone AccordionItem (no DisclosureGroup wrapper): a single
            controlled item honors `isExpanded` on mount, so `defaultExpanded`
            opens the card immediately. A DisclosureGroup would ignore the
            child's initial `isExpanded`. */}
        <AccordionItem isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
          {/* Overlay trigger pattern: the AccordionTrigger is an
                absolutely-positioned button that fills the whole row, so
                clicking anywhere — except on the GmailButton or
                Open-in-CRM <button>s, which selectively re-enable
                `pointer-events-auto` — toggles the disclosure. The
                visible content sits in a sibling layer with
                `pointer-events-none` so its clicks fall through to the
                overlay button below. */}
          <div className='relative'>
            {/* Only render the disclosure trigger when there's something
                  to expand. Otherwise the whole card becomes a no-op click
                  target with a useless chevron — see `isExpandable`. */}
            {isExpandable && (
              <AccordionTrigger
                aria-label={crmEventTriggerLabel({ isExpanded })}
                className='absolute inset-0 w-full focus-visible:ring-inset rounded'
              >
                {null}
              </AccordionTrigger>
            )}

            {/* Visible content layer. Header row + (separately) the
                  collapsed-state snippet, which lives BELOW the header
                  and spans the full card width — indented past the
                  avatar so it aligns with the header text on the left
                  and extends out to the card's right edge (under where
                  the side meta sits in the header row above). */}
            <div className='pointer-events-none relative'>
              <div
                className={cx(
                  'flex items-center gap-3 px-4 pt-4',
                  // When there's no collapsed preview row below, the
                  // card has no bottom padding — add it here so the
                  // header-only cards (e.g. generic entity types with
                  // no preview fields) don't render flush to the
                  // bottom border.
                  !previewNode && 'pb-4'
                )}
              >
                {!isRegistryCovered && cardIcon}
                <div
                  className={cx(
                    'flex-1 min-w-0',
                    // ActionEntityPreview wraps content in CrmClickableContent, which
                    // needs to receive clicks. The outer visible content layer is
                    // pointer-events-none (so clicks fall through to the absolutely-
                    // positioned AccordionTrigger overlay below). For registry-covered
                    // entity types there is no overlay (isExpandable is false), so we
                    // re-enable pointer events on the content slot itself.
                    isRegistryCovered && 'pointer-events-auto'
                  )}
                >
                  {renderHeader()}
                </div>

                <div className='flex items-center gap-2 text-xs text-tertiary flex-shrink-0'>
                  {formattedEmailDateTime && (
                    <span>{formattedEmailDateTime}</span>
                  )}
                  {/* Interactive buttons re-enable pointer-events so
                        they catch their own clicks instead of toggling
                        the disclosure. */}
                  {isEmailThread && emailThreadId && (
                    <span className='pointer-events-auto'>
                      <EmailProviderButton
                        threadId={emailThreadId}
                        provider={emailProvider}
                      />
                    </span>
                  )}
                  {(openSlideout || (isEmailThread && onOpenThread)) && (
                    <TriggerCardIconButton
                      icon={LayoutRight}
                      label={
                        isEmailThread && onOpenThread
                          ? 'View thread'
                          : 'Open in CRM'
                      }
                      isPressed={isRecordOpen}
                      onClick={handleOpenInCrm}
                    />
                  )}
                  {isExpandable && (
                    <AccordionChevron variant='default' className='w-4 h-4' />
                  )}
                </div>
              </div>

              {/* Full-width snippet, below the header. Inverse
                    grid-rows transition (1fr → 0fr) so the snippet
                    shrinks closed in the same animation window the main
                    panel below grows open. `inert` flips when expanded
                    so the (visually-empty) snippet container is removed
                    from the a11y tree. The 60px left inset
                    (p-4 + 32px avatar + 12px gap) makes it align with
                    the header text. */}
              {previewNode && (
                <div
                  inert={isExpanded ? true : undefined}
                  className='grid grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none group-data-[expanded]/item:grid-rows-[0fr]'
                >
                  <div className='min-h-0 overflow-hidden'>
                    <div className='pl-[60px] pr-4 pb-4 pt-1 text-xs text-tertiary opacity-100 transition-opacity duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none group-data-[expanded]/item:opacity-0'>
                      {previewNode}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Body in the panel — left-aligned to the header text
                (p-4 + 32px avatar + 12px gap = 60px inset). Lazy-mounted
                via `hasMounted` so collapsed cards on a list don't
                pre-render their iframes / heavy subtrees. */}
          <AccordionPanel className='pl-[60px] pr-4 pb-4 pt-1'>
            {hasMounted ? renderBody() : null}
          </AccordionPanel>
        </AccordionItem>
        {/* Screen-reader live region — aria-expanded already announces
            state changes via the trigger button, but consumer tests rely
            on this human-readable phrasing too. */}
        <div className='sr-only' aria-live='polite' aria-atomic='true'>
          {isExpanded
            ? 'CRM event preview expanded'
            : 'CRM event preview collapsed'}
        </div>
      </DetailPanelCard>

      {/* Connecting line to action below */}
      {showConnector && <TimelineConnector />}
    </div>
  );
}
